/** @jsxImportSource @emotion/react */
'use client'

import { css } from '@emotion/react'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ContactsIcon from '@mui/icons-material/Contacts'
import HandshakeIcon from '@mui/icons-material/Handshake'
import PsychologyIcon from '@mui/icons-material/Psychology'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import StorageIcon from '@mui/icons-material/Storage'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
// import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import {
	AICLAIMS_WELCOME_PATH,
} from '../config/aiclaims-routes'
import LoginModal from '../components/dashboard/LoginModal'
import Header from '../components/Header/Header'
import Hero from '../components/home/Hero'
import Container from '../components/home/Container'
import { DataCard, FlexCol, FlexRow } from '../components/layout'
import useAuth from '../hooks/useAuth'
import { parseHostnameFromEnv } from '../lib/brandFromHost'
import { resolvePostLoginPath } from '../lib/crossDomainAuth'
import { isWysehubAdminHost, isWysehubPortalHost } from '../lib/hostRouting'
import {
	buildAiClaimsPortalLoginUrl,
	isAiClaimsPortalHostname,
	isAiClaimsRootHostname,
} from '../lib/managedHosts'
import { canUseAiClaimsProduct, effectiveBrandAccess } from '../lib/pageAccess'
import { sanitizeInternalRedirect } from '../utils/safeRedirect'

function canAccessAiClaimsPortal(
	currentUser?: {
		brandAccess?: string | null
		rolenames?: readonly string[] | null
	} | null,
): boolean {
	return canUseAiClaimsProduct(effectiveBrandAccess(currentUser))
}

function PortalEntryPage({
	logoSrc,
	logoAlt,
	overline,
	title,
	description,
	ctaLabel,
	user,
	onSignIn,
}: {
	logoSrc: string
	logoAlt: string
	overline: string
	title: string
	description: string
	ctaLabel: string
	user: unknown
	onSignIn: () => void
}) {
	return (
		<Box
			// biome-ignore lint/plugin: full-viewport hero band
			sx={{ minHeight: '100vh' }}
		>
			<Container>
				<FlexCol
					gap="xl"
					align="center"
					justify="center"
					// biome-ignore lint/plugin: centred hero copy
					sx={{ textAlign: 'center' }}
				>
					<Box
						component="img"
						src={logoSrc}
						alt={logoAlt}
						// biome-ignore lint/plugin: logo media dimensions
						sx={{ width: 220, maxWidth: '70vw' }}
					/>
					<FlexCol
						gap="md"
						// biome-ignore lint/plugin: hero copy column max-width
						sx={{ maxWidth: 720 }}
					>
						<Typography variant="overline">{overline}</Typography>
						<Typography variant="display">{title}</Typography>
						<Typography variant="body1" color="text.secondary">
							{description}
						</Typography>
					</FlexCol>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
						<Button variant="contained" onClick={onSignIn}>
							{user ? ctaLabel : `Sign in to ${ctaLabel}`}
						</Button>
						<Button variant="outlined" component="a" href="/privacy">
							Privacy
						</Button>
					</Stack>
				</FlexCol>
			</Container>
		</Box>
	)
}

const FEATURES = [
	{
		icon: <PsychologyIcon color="success" fontSize="large" />,
		title: 'Intelligent Partner Matching',
		description:
			'Our platform connects each claim with the best-suited outsourced partner for optimal outcomes and customer satisfaction.',
	},
	{
		icon: <HandshakeIcon color="success" fontSize="large" />,
		title: 'Seamless Integration',
		description:
			'Effortlessly integrate with our system to create a unified, efficient, and transparent claims processing experience.',
	},
	{
		icon: <StorageIcon color="success" fontSize="large" />,
		title: 'Data Driven Decisions',
		description:
			'Harness the power of data to enhance accuracy and speed in claims handling, reducing turnaround times and increasing conversion.',
	},
	{
		icon: <AssessmentIcon color="success" fontSize="large" />,
		title: 'On Demand Stats',
		description:
			'Access our web-based reporting software for KPIs, unique AI-based claim scores, and Wysehub comparisons. Generate, edit, and export reports all from our platform.',
	},
	{
		icon: <RocketLaunchIcon color="success" fontSize="large" />,
		title: 'Scalable Solutions',
		description:
			'Scale your operations seamlessly with our availability and range of outsourced partners instantly tailored to your needs.',
	},
]

function HomePage() {
	const router = useRouter()
	const [isMounted, setIsMounted] = useState(false)
	const [openLoginModal, setOpenLoginModal] = useState(false)
	const [isPlaying, setIsPlaying] = useState(false)
	const { user } = useAuth()
	const portalHostname = parseHostnameFromEnv(
		process.env.NEXT_PUBLIC_AICLAIMS_PORTAL_HOST,
	)
	const isAiClaimsRootHost =
		isMounted && isAiClaimsRootHostname(window.location.hostname)
	const isAiClaimsPortalHost =
		isMounted &&
		(isAiClaimsPortalHostname(window.location.hostname) ||
			(!!portalHostname &&
				window.location.hostname.toLowerCase() ===
					portalHostname.toLowerCase()))
	const isWysehubPortalSubdomain =
		isMounted && isWysehubPortalHost(window.location.hostname)
	const isWysehubAdminSubdomain =
		isMounted && isWysehubAdminHost(window.location.hostname)

	useEffect(() => {
		setIsMounted(true)
	}, [])

	useEffect(() => {
		const currentSearchParams = new URLSearchParams(window.location.search)
		const shouldOpenLogin = currentSearchParams.get('login') === 'true'

		if (isAiClaimsPortalHost && user && canAccessAiClaimsPortal(user)) {
			router.replace(AICLAIMS_WELCOME_PATH)
			return
		}

		if (isWysehubPortalSubdomain && user) {
			router.replace(
				resolvePostLoginPath(
					effectiveBrandAccess(user),
					window.location.hostname,
				),
			)
			return
		}

		if (isWysehubAdminSubdomain && user) {
			router.replace('/admin')
			return
		}

		if (shouldOpenLogin && !user) {
			setOpenLoginModal(true)

			const nextUrl = sanitizeInternalRedirect(
				currentSearchParams.get('next'),
				window.location.origin,
			)
			if (nextUrl) {
				localStorage.setItem('loginRedirectUrl', nextUrl)
			} else {
				localStorage.removeItem('loginRedirectUrl')
			}
		} else if (shouldOpenLogin && user) {
			const nextUrl = sanitizeInternalRedirect(
				currentSearchParams.get('next'),
				window.location.origin,
			)
			if (nextUrl) {
				router.push(nextUrl)
			}
		}
	}, [
		isAiClaimsPortalHost,
		isAiClaimsRootHost,
		isWysehubAdminSubdomain,
		isWysehubPortalSubdomain,
		user,
		router,
	])

	const handleHomeHubClick = () => {
		if (isAiClaimsRootHost) {
			window.location.href = buildAiClaimsPortalLoginUrl(
				window.location.hostname,
				window.location.protocol,
			)
			return
		}

		if (isAiClaimsPortalHost) {
			if (user) {
				if (canAccessAiClaimsPortal(user)) {
					router.push(AICLAIMS_WELCOME_PATH)
				} else {
					router.replace('/')
				}
			} else {
				setOpenLoginModal(true)
			}
			return
		}

		if (isWysehubAdminSubdomain) {
			if (user) {
				router.push('/admin')
			} else {
				setOpenLoginModal(true)
			}
			return
		}

		if (isWysehubPortalSubdomain) {
			if (user) {
				router.push('/homehub')
			} else {
				setOpenLoginModal(true)
			}
			return
		}

		if (user) {
			router.push('/homehub')
		} else {
			setOpenLoginModal(true)
		}
	}

	const handleCloseLoginModal = () => setOpenLoginModal(false)

	const videoRef = useRef<HTMLVideoElement | null>(null)
	const handlePlayVideo = () => {
		setIsPlaying(true)
		void videoRef.current?.play()
	}

	const styles = {
		icon: css`
			padding: 10px;
			background: rgb(166 197 166 / 20%);
			border-radius: 6px;
			width: 50px;
			height: 50px;
			display: flex;
			align-items: center;
			justify-content: center;

			svg {
				width: 30px;
				height: 30px
			}
		`
	};

	if (!isMounted) {
		return (
			<Container>
				<Stack
					spacing={3}
					// biome-ignore lint/plugin: layout styling kept in sx (MUI v9 removed direct system props)
					sx={{
						alignItems: 'center',
					}}
				>
					<Skeleton variant="rectangular" width={220} height={64} />
					<Skeleton variant="text" width="60%" height={56} />
					<Skeleton variant="text" width="80%" height={32} />
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
						<Skeleton variant="rounded" width={180} height={44} />
						<Skeleton variant="rounded" width={120} height={44} />
					</Stack>
				</Stack>
			</Container>
		)
	}

	if (isAiClaimsPortalHost) {
		return (
			<>
				<PortalEntryPage
					logoSrc="/AiClaimsBlackLogo.svg"
					logoAlt="AI Claims logo"
					overline="Portal Access"
					title="Access the AI Claims operations portal."
					description="Sign in to review dashboards, recent runs, workflows, cases, and operational cost views for the AI Claims environment."
					ctaLabel="Open Portal"
					user={user}
					onSignIn={handleHomeHubClick}
				/>
				<LoginModal open={openLoginModal} onClose={handleCloseLoginModal} />
			</>
		)
	}

	if (isWysehubPortalSubdomain || isWysehubAdminSubdomain) {
		return (
			<>
				<PortalEntryPage
					logoSrc="/assets/images/WH-logo.png"
					logoAlt="WyseHub logo"
					overline={isWysehubAdminSubdomain ? 'Admin Access' : 'Portal Access'}
					title={
						isWysehubAdminSubdomain
							? 'Access the WyseHub administration area.'
							: 'Access the WyseHub partner portal.'
					}
					description={
						isWysehubAdminSubdomain
							? 'Sign in with a system administration account to manage users, partners, settings, workflows, and operational controls.'
							: 'Sign in to review referrals, claims, dashboards, requests, and daily operational activity across the WyseHub platform.'
					}
					ctaLabel={isWysehubAdminSubdomain ? 'Open Admin' : 'Open Portal'}
					user={user}
					onSignIn={handleHomeHubClick}
				/>
				<LoginModal open={openLoginModal} onClose={handleCloseLoginModal} />
			</>
		)
	}

	return (
		<>
			<Header user={user} onLoginClick={handleHomeHubClick} />
			<Box component="main">
				{/* Hero */}
				<Hero onCtaClick={handleHomeHubClick} />

				<Box
					// biome-ignore lint/plugin: full-width page canvas with responsive gutters
					sx={{ p: 3 }}
				>
					<Container>
						<FlexCol gap="lg">
							{/* Feature cards */}
							<Grid container spacing={3}>
								{FEATURES.map((item) => (
									<Grid key={item.title} size={{ xs: 12, md: 4 }}>
										<DataCard
											// biome-ignore lint/plugin: stretch the card to fill its grid row height
											sx={{ height: '100%' }}
										>
											<FlexCol gap="md" align="start">
												<span css={styles.icon}>{item.icon}</span>
												<Typography variant="h2">{item.title}</Typography>
												<Typography variant="body1" color="text.secondary">
													{item.description}
												</Typography>
											</FlexCol>
										</DataCard>
									</Grid>
								))}
							</Grid>

							{/* Video section */}
							<DataCard noPadding>
								<Box
									// biome-ignore lint/plugin: video stage at the source aspect ratio with a centred play overlay
									sx={{
										position: 'relative',
										width: '100%',
										aspectRatio: '960 / 459',
									}}
								>
									<Box
										data-testid="video-player"
										component="video"
										ref={videoRef}
										src="https://whvideo.ams3.cdn.digitaloceanspaces.com/-MAIN%20COMP_5.mp4"
										muted
										loop
										playsInline
										preload="metadata"
										// biome-ignore lint/plugin: video fills the stage; the first frame acts as the poster
										sx={{
											position: 'absolute',
											inset: 0,
											width: '100%',
											height: '100%',
											objectFit: 'cover',
											display: 'block',
										}}
									/>
									{!isPlaying && (
										<Box
											// biome-ignore lint/plugin: absolutely positioned centred play-button overlay
											sx={{
												position: 'absolute',
												inset: 0,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<Button variant="contained" onClick={handlePlayVideo}>
												Watch Our Video
											</Button>
										</Box>
									)}
								</Box>
							</DataCard>

							{/* AnnieAi + Contact */}
							<Grid container spacing={3}>
								<Grid size={{ xs: 12, md: 6 }}>
									<DataCard
										// biome-ignore lint/plugin: stretch the card to fill its grid row height
										sx={{ height: '100%' }}
									>
										<FlexCol gap="md" align="start">
											<Box
												component="img"
												src="/assets/images/AA_Logo.png"
												alt="AnnieAi logo"
												loading="lazy"
												// biome-ignore lint/plugin: logo media dimension
												sx={{ maxWidth: 120 }}
											/>
											<Typography variant="body1" color="text.secondary">
												We have teamed up with AnnieAi to provide the latest machine
												learning technology for greater accuracy in claims scoring.
											</Typography>
										</FlexCol>
									</DataCard>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<DataCard
										// biome-ignore lint/plugin: stretch the card to fill its grid row height
										sx={{ height: '100%' }}
									>
										<FlexCol gap="md" align="start">
											<span css={styles.icon}>
												<ContactsIcon color="success" fontSize="large" />
											</span>
											<Typography variant="h2">
												Let&apos;s start a conversation
											</Typography>
											<Typography variant="body1" color="text.secondary">
												Get in contact with us to discuss how Wysehub can help you.
											</Typography>
										</FlexCol>
									</DataCard>
								</Grid>
							</Grid>

							{/* Footer logos */}
							<FlexRow justify="center" align="center" gap="md">
								<Box
									component="img"
									src="/assets/images/WH-logo.png"
									alt="WyseHub logo"
									// biome-ignore lint/plugin: logo media dimension
									sx={{ maxWidth: 150 }}
								/>
								<Typography variant="h5">x</Typography>
								<Box
									component="img"
									src="/assets/images/AA_Logo.png"
									alt="AnnieAi logo"
									// biome-ignore lint/plugin: logo media dimension
									sx={{ maxWidth: 150 }}
								/>
							</FlexRow>
						</FlexCol>
					</Container>
				</Box>
			</Box>
			{/* Footer bar */}
			<Box
				// biome-ignore lint/plugin: full-width footer band with a top divider
				sx={{ borderTop: '1px solid', borderColor: 'divider', py: 3, mt: 2 }}
			>
				<Typography variant="body2" align="center" color="text.secondary">
					© 2024 WyseHub. All rights reserved.
				</Typography>
			</Box>
			<LoginModal open={openLoginModal} onClose={handleCloseLoginModal} />
		</>
	)
}

export default HomePage
